import Add from '@mui/icons-material/Add';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import SensorsOutlined from '@mui/icons-material/SensorsOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import MotiveLogo from '~images/telematics/motive.svg?react';
// Interim PNG — the official Samsara SVG is not yet available, so we ship a trimmed
// raster for now. Swap to the SVGR `?react` idiom (like Motive) is tracked in TRE-15317.
import samsaraLogo from '~images/telematics/samsara.png';

// Public telematics integration docs, opened from the "Learn more" link (new tab).
const CATENA_DOCS_URL = 'https://docs.tread.ai/integrations/telematics';

// Each onboarding step pairs an icon with a translated title and description,
// rendered as numbered cards so the connection flow is immediately obvious.
const ONBOARDING_STEPS = [
  {
    Icon: FactCheckOutlined,
    titleKey: 'administration.integration.catena.empty_state.step_choose',
    descriptionKey:
      'administration.integration.catena.empty_state.step_choose_description',
  },
  {
    Icon: LockOutlined,
    titleKey: 'administration.integration.catena.empty_state.step_authorize',
    descriptionKey:
      'administration.integration.catena.empty_state.step_authorize_description',
  },
  {
    Icon: SensorsOutlined,
    titleKey: 'administration.integration.catena.empty_state.step_live',
    descriptionKey: 'administration.integration.catena.empty_state.step_live_description',
  },
] as const;

interface CatenaEmptyStateProps {
  disabled: boolean;
  isConnecting: boolean;
  onConnect: () => void;
}

const CatenaEmptyState = ({
  disabled,
  isConnecting,
  onConnect,
}: CatenaEmptyStateProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.semantic.surfaceRaised,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 3,
        // Clip the tinted provider panel and step band to the card's rounded corners.
        overflow: 'hidden',
        boxShadow: 1,
        mb: 3,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }}>
        {/* Tinted provider showcase — decorative; the headline carries the meaning. */}
        <Box
          aria-hidden
          sx={{
            backgroundColor: theme.brandV2.colors.treadGray8,
            borderBottom: {
              xs: `1px solid ${theme.brandV2.colors.treadGray7}`,
              md: 'none',
            },
            borderRight: {
              xs: 'none',
              md: `1px solid ${theme.brandV2.colors.treadGray7}`,
            },
            flexShrink: 0,
            p: 3,
            width: { xs: '100%', md: 240 },
          }}
        >
          <Stack spacing={1.25}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: theme.semantic.surfaceRaised,
                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                borderRadius: 2.5,
                boxShadow: 1,
                display: 'flex',
                height: 78,
                justifyContent: 'center',
                px: 2,
              }}
            >
              <Box
                component="img"
                data-test-id="catena-samsara-logo"
                src={samsaraLogo}
                sx={{ display: 'block', maxHeight: 48, maxWidth: '100%', width: 'auto' }}
              />
            </Box>
            <Box
              data-test-id="catena-motive-logo"
              sx={{
                alignItems: 'center',
                backgroundColor: theme.semantic.surfaceRaised,
                border: `1px solid ${theme.brandV2.colors.treadGray7}`,
                borderRadius: 2.5,
                boxShadow: 1,
                display: 'flex',
                height: 78,
                justifyContent: 'center',
                px: 2,
                '& svg': { display: 'block', height: 26, width: 'auto' },
              }}
            >
              <MotiveLogo />
            </Box>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: theme.brandV2.colors.treadGray2,
              fontWeight: 600,
              mt: 1.5,
              textAlign: 'center',
            }}
          >
            {t('administration.integration.catena.empty_state.providers_label')}
          </Typography>
        </Box>

        {/* White content zone — headline, supporting copy, and the primary action. */}
        <Box sx={{ minWidth: 0, p: { xs: 3, md: 4 } }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 1 }}
          >
            {t('administration.integration.catena.empty_state.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.brandV2.colors.treadGray2, maxWidth: '52ch', mb: 2.5 }}
          >
            {t('administration.integration.catena.empty_state.description')}
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={disabled}
              loading={isConnecting}
              onClick={onConnect}
              startIcon={<Add />}
            >
              {t('administration.integration.catena.empty_state.cta')}
            </SmallLoadingButton>
            <Link
              href={CATENA_DOCS_URL}
              rel="noopener noreferrer"
              target="_blank"
              sx={{
                alignItems: 'center',
                color: theme.palette.info.main,
                display: 'inline-flex',
                fontSize: 14,
                fontWeight: 600,
                gap: 0.5,
                textDecorationColor: 'inherit',
                textUnderlineOffset: '4px',
              }}
            >
              {t('administration.integration.catena.empty_state.learn_more')}
              <OpenInNew sx={{ fontSize: 16 }} />
            </Link>
          </Stack>
        </Box>
      </Stack>

      {/* Tinted step band — separates the "how to connect" flow from the pitch above. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          backgroundColor: theme.brandV2.colors.treadGray9,
          borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`,
          p: { xs: 3, md: 4 },
        }}
      >
        {ONBOARDING_STEPS.map(({ Icon, titleKey, descriptionKey }, index) => (
          <Box
            key={titleKey}
            sx={{
              backgroundColor: theme.semantic.surfaceRaised,
              border: `1px solid ${theme.brandV2.colors.treadGray7}`,
              borderRadius: 2,
              boxShadow: 1,
              flex: 1,
              p: 2,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  alignItems: 'center',
                  backgroundColor: theme.brandV2.colors.treadGray1,
                  borderRadius: '50%',
                  color: theme.semantic.textInverse,
                  display: 'flex',
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  height: 22,
                  justifyContent: 'center',
                  width: 22,
                }}
              >
                {index + 1}
              </Box>
              <Icon sx={{ color: theme.brandV2.colors.treadGray3, fontSize: 18 }} />
              <Typography
                variant="body2"
                sx={{ color: theme.brandV2.colors.treadGray1, fontWeight: 600 }}
              >
                {t(titleKey)}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray2 }}>
              {t(descriptionKey)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default CatenaEmptyState;
